import React, {
  ReactNode,
} from 'react';
import clsx from 'clsx';
import Avatar, {
  AvatarProps,
} from '@mui/material/Avatar';
import {
  getColorForUnique,
} from 'utils/colors';
import {
  getUserInitials,
  notEmptyString,
} from '@populateui/utils';

const imageSizes: readonly string[] = [
  'xs',
  'sm',
  'base',
  'md',
  'lg',
  'xl',
  'xxl',
  '2xl',
  'fit',
] as const;

export type ImageSize = typeof imageSizes[number];

export type Props = Pick<AvatarProps, 'classes' | 'variant' | 'className' | 'onClick'> & {
  size?: ImageSize;
  placeholder?: string;
  src?: string;
  alt?: string;
  actions?: ReactNode;
  children?: ReactNode;
  color?: string;
  title?: string;
};

const sizes: Readonly<Record<ImageSize, (number | string)[]>> = Object.freeze({
  '2xs': [20, 8],
  xs: [20, 10],
  sm: [24, 11],
  base: [26, 12],
  regular: [32, 12],
  md: [40, 18],
  lg: [64, 24],
  xl: [72, 32],
  xxl: [96, 36],
  '2xl': [96, 36],
  fit: ['100% !important', 32],
});

export default function Image({
  placeholder,
  size = 'sm',
  src,
  alt,
  children,
  variant = 'circular',
  classes: _classes,
  className,
  onClick,
  color,
}: Props) {
  const [wh, fontSize] = sizes[size];
  const imageSrc = src ?? placeholder;
  const hasImageSrc = notEmptyString(imageSrc);
  const classes = {
    ..._classes,
    root: clsx({
      'flex-1': size === 'fit',
      'ring-1 ring-inset ring-black/10 shadow-2xs': hasImageSrc,
    }, className, _classes?.root),
    img: clsx({
      '!w-[initial] !h-auto !max-h-full': size === 'fit',
      'object-cover': size !== 'fit',
    },
    '!overflow-initial',
    _classes?.img),
  };
  const initials = getUserInitials(alt ?? '');
  const displayInitials = notEmptyString(children)
    ? children
    : initials;
  return (
    <Avatar
      sx={{
        width: size === 'fit' ? '100%' : wh,
        height: size === 'fit' ? 'auto' : wh,
        fontSize,
        bgcolor: hasImageSrc ? 'transparent' : (color ?? getColorForUnique(alt ?? '')),
      }}
      classes={classes}
      src={hasImageSrc ? imageSrc : undefined}
      alt={alt}
      variant={variant}
      onClick={onClick}
      slotProps={{
        img: {
          loading: 'lazy',
          style: {
            overflow: 'initial',
            borderRadius: 'inherit',
          },
        },
      }}
    >
      {displayInitials}
    </Avatar>
  );
}
